import { motion } from "framer-motion";
import { Button } from "./ui/Button";
import { Hexagon } from "./Hexagon";
import { trackEvent } from "../lib/analytics";

interface HeroScreenProps {
  onStart: () => void;
  refProfile?: string | null;
  liveCount?: number | null;
}

// Positions/tailles fixes pour les ~12 hexagones décoratifs (mobile-first).
// Top/left en %, side en px, opacité dans la palette text-white/[…].
const HEX = [
  { top: "8%", left: "12%", size: 14, op: "text-white/[0.08]", rot: 12 },
  { top: "16%", left: "82%", size: 22, op: "text-vert-eau/30", rot: -8 },
  { top: "26%", left: "5%", size: 10, op: "text-white/[0.06]", rot: 0 },
  { top: "34%", left: "88%", size: 16, op: "text-white/[0.07]", rot: 22 },
  { top: "44%", left: "10%", size: 28, op: "text-jade/30", rot: 8 },
  { top: "52%", left: "78%", size: 12, op: "text-white/[0.09]", rot: -14 },
  { top: "62%", left: "20%", size: 18, op: "text-white/[0.05]", rot: 0 },
  { top: "70%", left: "85%", size: 14, op: "text-vert-eau/25", rot: 18 },
  { top: "78%", left: "8%", size: 20, op: "text-white/[0.06]", rot: -10 },
  { top: "84%", left: "70%", size: 10, op: "text-white/[0.08]", rot: 6 },
  { top: "90%", left: "30%", size: 24, op: "text-jade/25", rot: -4 },
  { top: "12%", left: "50%", size: 8, op: "text-white/[0.07]", rot: 30 },
];

export function HeroScreen({ onStart, refProfile, liveCount }: HeroScreenProps) {
  const handleClick = () => {
    trackEvent("quiz_start");
    onStart();
  };

  return (
    <section className="relative min-h-[100svh] gradient-hero overflow-hidden flex flex-col items-center justify-center px-6 py-12 text-white">
      {/* Décor hexagones */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {HEX.map((h, i) => (
          <span
            key={i}
            className={`absolute ${h.op}`}
            style={{ top: h.top, left: h.left, transform: `translate(-50%, -50%) rotate(${h.rot}deg)` }}
          >
            <Hexagon size={h.size} />
          </span>
        ))}
      </div>

      {/* Sticker proof social */}
      {refProfile && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur text-xs font-body z-10"
        >
          Ton pote {refProfile} t'a invité 👀
        </motion.div>
      )}

      {/* Logo animé */}
      <motion.img
        src="/logo-icon-white.png"
        alt="Ommistack"
        width={160}
        height={160}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.1 }}
        className="mb-8 relative z-10 h-40 w-40 object-contain"
      />

      {/* Titre */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="font-heading font-black text-[1.75rem] sm:text-5xl text-center leading-tight whitespace-nowrap relative z-10"
      >
        Quel investisseur <span className="text-aurore">es-tu ?</span>
      </motion.h1>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-4 text-center font-body text-base sm:text-lg max-w-md text-white/85 relative z-10"
      >
        8 questions fun pour découvrir ton profil.
        <br />
        <span className="text-vert-eau font-semibold">2 min chrono ⏱️</span>
      </motion.p>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.7, type: "spring" }}
        className="mt-10 relative z-10"
      >
        <Button
          variant="cta"
          size="lg"
          onClick={handleClick}
          className="animate-pulse-glow text-base sm:text-lg"
        >
          Je fais le quiz 🚀
        </Button>
      </motion.div>

      {/* Mention */}
      <p className="mt-6 text-xs text-white/50 relative z-10">Gratuit • 100% fun</p>

      {liveCount != null && liveCount > 0 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="mt-3 text-xs text-white/70 relative z-10"
        >
          🔥 {liveCount.toLocaleString("fr-FR")} personnes ont déjà découvert leur profil
        </motion.p>
      )}

      {/* Wordmark bas */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 opacity-40 z-10">
        <span className="font-heading font-black tracking-wide text-sm text-white">OMMISTACK</span>
      </div>
    </section>
  );
}
